import { memo } from 'react';
import { FILTER_SLIDEOUT_OPTION_ROW } from './filterSlideoutTokens.js';
import { joinClasses } from './filterSlideout.utils.js';
import { Check } from 'lucide-react';

const CONTROL_BASE =
  'inline-flex shrink-0 items-center justify-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50';

function CheckboxControl({ checked, disabled }) {
  return (
    <span
      className={joinClasses(
        CONTROL_BASE,
        'h-4 w-4 rounded-sm',
        checked ? 'border-2 border-[#2173FF] bg-white text-[#2173FF]' : 'border border-[#5C5C5C] bg-white',
        disabled ? 'opacity-50' : '',
      )}
      aria-hidden
    >
      {checked ? <Check size={12} strokeWidth={2.5} /> : null}
    </span>
  );
}

function RadioControl({ checked, disabled }) {
  return (
    <span
      className={joinClasses(
        CONTROL_BASE,
        'h-4 w-4 rounded-full border bg-white p-[3px]',
        checked ? 'border-[#2173FF]' : 'border-[#5C5C5C]',
        disabled ? 'opacity-50' : '',
      )}
      aria-hidden
    >
      {checked ? <span className="h-full w-full rounded-full bg-[#2173FF]" /> : null}
    </span>
  );
}

function SwitchControl({ checked, disabled }) {
  return (
    <span
      className={joinClasses(
        'inline-flex shrink-0 items-center justify-start rounded-full p-[2px] transition-colors',
        'h-4 w-[26px]',
        checked ? 'bg-[#2173FF]' : 'border-2 border-[#5C5C5C] bg-[#f2f2f2]',
        disabled ? 'opacity-50' : '',
      )}
      aria-hidden
    >
      <span
        className={joinClasses(
          'shrink-0 rounded-full transition-all duration-200',
          checked ? 'h-3 w-3 translate-x-[10px] bg-white' : 'h-2 w-2 bg-[#2a2a2a]',
        )}
      />
    </span>
  );
}

function resolveControl(type, checked, disabled) {
  if (type === 'radio') return <RadioControl checked={checked} disabled={disabled} />;
  if (type === 'switch') return <SwitchControl checked={checked} disabled={disabled} />;
  return <CheckboxControl checked={checked} disabled={disabled} />;
}

/**
 * @param {object} props
 * @param {'checkbox'|'radio'|'switch'} [props.control='checkbox']
 * @param {boolean} props.checked
 * @param {boolean} [props.disabled=false]
 * @param {import('react').ReactNode} props.label
 * @param {string} [props.description]
 * @param {string} [props.color]
 * @param {() => void} [props.onToggle]
 */
const FilterSlideoutOptionRow = ({
  control = 'checkbox',
  checked,
  disabled = false,
  label,
  description,
  color,
  onToggle,
}) => {
  const role = control === 'switch' ? 'switch' : control;

  return (
    <button
      type="button"
      role={role}
      aria-checked={checked}
      disabled={disabled}
      onClick={onToggle}
      className={joinClasses(
        FILTER_SLIDEOUT_OPTION_ROW,
        'rounded text-left transition-colors hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50',
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
      )}
    >
      {resolveControl(control, checked, disabled)}
      {color ? (
        <span
          className="h-2 w-2 shrink-0 rounded-full"
          style={{ backgroundColor: color }}
          aria-hidden
        />
      ) : null}
      <span className="min-w-0 flex-1">
        <span className="block truncate">{label}</span>
        {description ? (
          <span className="block truncate text-xs text-content-muted">{description}</span>
        ) : null}
      </span>
    </button>
  );
};

export default memo(FilterSlideoutOptionRow);
